import React, { useEffect, useState } from 'react';
import { cloneDeep, omit } from 'lodash';
import { styled } from '@mui/material';
import uuid from 'uuid';

import { DrawerActions, TemplateTaskView, TemplateSubtaskView } from 'components/organisms/TaskTemplates';
import { useInstructor } from 'contexts/instructorRole';
import { Button, Icon, IconButton, Stack, Text } from 'shell/components';

const TemplateDrawerInner = ({
    isRenderedInMilestone = false,
    isOpen = false,
    template = {},
    files,
    sharedUrls,
    changes,
    groups = [],
    spawnedTasks,
    updateTemplate,
    setFiles,
    setSharedUrls,
    setChanges,
    setDrawerState,
    setUploadResources,
    setConfirmationModal,
    closeConfirmationModal,
    setScheduleModal,
    save,
    publish,
    unpublish,
    unschedule,
    deleteTemplate,
}) => {
    const { milestones } = useInstructor();

    const [view, setView] = useState('task');
    const [activeSubtaskId, setActiveSubtaskId] = useState('');

    const { published_on } = template;
    const templateState = !published_on ? 'draft' : published_on < new Date().getTime() ? 'published' : 'scheduled';

    const handleClickBackSubtask = () => {
        setView('task');
        setActiveSubtaskId('');
    };

    const handleClickBackTemplate = () => {
        if (template.isLocal) {
            setConfirmationModal({
                visible: true,
                destructive: true,
                title: 'Leave Task Creation?',
                content: 'All changes made to this task will not be saved.',
                confirmText: 'Leave',
                onConfirm: () => {
                    closeConfirmationModal();
                    setDrawerState((current) => ({ ...current, isOpen: false }));
                    deleteTemplate(template.id);
                },
                onCancel: closeConfirmationModal,
            });
        } else {
            if (!Boolean(Object.keys(omit(changes, ['published_on'])).length)) {
                setChanges({});
                setDrawerState((current) => ({ ...current, isOpen: false }));
            } else {
                setConfirmationModal({
                    visible: true,
                    destructive: true,
                    title: 'Discard Changes?',
                    content: 'All changes made to this task will not be saved.',
                    confirmText: 'Leave',
                    onConfirm: () => {
                        closeConfirmationModal();
                        setDrawerState((current) => ({ ...current, isOpen: false }));
                        setChanges({});
                    },
                    onCancel: closeConfirmationModal,
                });
            }
        }
    };

    const handleClickBack = () => {
        if (view === 'subtask') {
            handleClickBackSubtask();
        } else {
            handleClickBackTemplate();
        }
    };

    const handleTemplateChange = (key, value) => {
        if (template.isLocal) {
            const newTemplate = cloneDeep(template);
            newTemplate.modified_on = new Date().getTime();
            if (key === 'visible_to') {
                if (newTemplate.visible_to.includes(value)) {
                    newTemplate.visible_to = newTemplate.visible_to.filter((groupId) => groupId !== value);
                } else {
                    newTemplate.visible_to.push(value);
                }
            } else {
                newTemplate[key] = value;
            }
            updateTemplate(template.id, newTemplate);
        } else {
            setChanges((prev) => {
                if (key === 'visible_to') {
                    return prev.visible_to
                        ? {
                              ...prev,
                              visible_to: prev.visible_to.includes(value)
                                  ? prev.visible_to.filter((groupId) => {
                                        return groupId !== value;
                                    })
                                  : [...(prev.visible_to || []), value],
                          }
                        : {
                              ...prev,
                              visible_to: template.visible_to.includes(value)
                                  ? template.visible_to.filter((groupId) => {
                                        return groupId !== value;
                                    })
                                  : [...template.visible_to, value],
                          };
                } else {
                    return {
                        ...prev,
                        [key]: value,
                    };
                }
            });
        }
    };

    const handleAddResources = () => {
        setUploadResources((current) => ({ ...current, isOpen: true }));
    };

    const handleDeleteResource = (resourceType, resourceId) => {
        switch (resourceType) {
            case 'file':
                setFiles((current) => ({
                    ...current,
                    [resourceId]: { ...current[resourceId], active: false },
                }));
                break;
            case 'link':
                setSharedUrls((current) => ({
                    ...current,
                    [resourceId]: { ...current[resourceId], active: false },
                }));
                break;
            default:
                break;
        }

        if (template.isLocal) {
            const resources = template.resources.filter((resource) => resource.id !== resourceId);
            updateTemplate(template.id, { ...template, resources });
        } else {
            setChanges((current) => {
                const resource = (current.resources || []).find((resource) => resource.id === resourceId);
                if (resource) {
                    return {
                        ...current,
                        resources:
                            resource.action === 'add'
                                ? current.resources.filter((resource) => resource.id !== resourceId)
                                : current.resources.map((resource) => {
                                      return resource.id === resourceId ? { ...resource, action: 'delete' } : resource;
                                  }),
                    };
                } else {
                    const targetResource = [...Object.values(files), ...Object.values(sharedUrls)].find(
                        (resource) => resource.id === resourceId
                    );
                    return {
                        ...current,
                        resources: [
                            ...(current.resources || []),
                            {
                                ...targetResource,
                                action: 'delete',
                                type: targetResource.data ? 'file' : 'link',
                            },
                        ],
                    };
                }
            });
        }
    };

    const handleClickSubtask = (subtaskId) => {
        setActiveSubtaskId(subtaskId);
        setView('subtask');
    };

    const handleAddSubtask = () => {
        const id = uuid.v4();
        const newSubtask = {
            id,
            created_on: new Date().getTime(),
            title: '',
            description: '',
            workload: 'low',
            end_on: new Date().getTime(),
            subtask_ids: [],
        };
        if (template.isLocal) {
            const newTemplate = cloneDeep(template);
            newTemplate.subtasks.push(newSubtask);
            updateTemplate(template.id, newTemplate);
        } else {
            setChanges((prev) => {
                return {
                    ...prev,
                    subtasks: [...(prev.subtasks || []), { ...newSubtask, action: 'add' }],
                };
            });
        }
        setActiveSubtaskId(id);
        setView('subtask');
    };

    const handleDeleteSubtask = (subtaskId) => {
        if (template.isLocal) {
            const subtasks = cloneDeep(template.subtasks).filter((subtask) => subtask.id !== subtaskId);
            updateTemplate(template.id, { ...template, subtasks });
        } else {
            setChanges((prev) => {
                const subtask = (prev.subtasks || []).find((subtask) => subtask.id === subtaskId);
                return {
                    ...prev,
                    subtasks: subtask
                        ? subtask.action === 'add'
                            ? prev.subtasks.filter((subtask) => subtask.id !== subtaskId)
                            : prev.subtasks.map((subtask) =>
                                  subtask.id === subtaskId ? { ...subtask, action: 'delete' } : subtask
                              )
                        : [
                              ...(prev.subtasks || []),
                              { ...template.subtasks.find((subtask) => subtask.id === subtaskId), action: 'delete' },
                          ],
                };
            });
        }
    };

    const handleSubtaskChange = (key, value) => {
        if (template.isLocal) {
            const subtasks = cloneDeep(template.subtasks).map((subtask) => {
                return subtask.id === activeSubtaskId
                    ? {
                          ...subtask,
                          [key]: value,
                      }
                    : subtask;
            });
            updateTemplate(template.id, { subtasks });
        } else {
            setChanges((prev) => {
                return {
                    ...prev,
                    subtasks: (prev.subtasks || []).some((subtask) => subtask.id === activeSubtaskId)
                        ? prev.subtasks.map((subtask) => {
                              return subtask.id === activeSubtaskId ? { ...subtask, [key]: value } : subtask;
                          })
                        : [
                              ...(prev.subtasks || []),
                              {
                                  ...template.subtasks.find((subtask) => subtask.id === activeSubtaskId),
                                  [key]: value,
                                  action: 'edit',
                              },
                          ],
                };
            });
        }
    };

    const renderBreadcrumbs = () => {
        const milestone = isRenderedInMilestone ? milestones.find(({ id }) => id === template.milestone_id) : null;
        let subtask = null;
        if (isRenderedInMilestone) {
            subtask = view === 'subtask' ? template.subtasks.find(({ id }) => id === activeSubtaskId) : null;
        } else {
            subtask =
                template.subtasks?.find(({ id }) => id === activeSubtaskId) ||
                changes.subtasks?.find(({ id }) => id === activeSubtaskId);
        }
        return milestone || subtask ? (
            <BreadcrumbsStack direction={'row'} spacing={1}>
                {milestone && (
                    <>
                        <Button
                            color={'neutral'}
                            startIcon={<Icon kind={'starEmpty'} />}
                            variant={'borderless'}
                            onClick={handleClickBackTemplate}
                        >
                            <Text
                                sx={{ textDecoration: 'underline' }}
                                color={'inherit'}
                                noWrap
                                title={milestone.title || 'Untitled Milestone'}
                                truncate={30}
                                variant={'p2'}
                            >
                                {milestone.title || 'Untitled Milestone'}
                            </Text>
                        </Button>
                        <Icon kind={'chevronRight'} />
                    </>
                )}
                <Button color={'neutral'} variant={'borderless'} onClick={handleClickBackSubtask}>
                    <Text
                        sx={{ textDecoration: 'underline' }}
                        color={'inherit'}
                        noWrap
                        title={template.title || 'Untitled Task'}
                        truncate={30}
                        variant={'p2'}
                    >
                        {template.title || 'Untitled Task'}
                    </Text>
                </Button>
                {subtask && (
                    <>
                        <Icon kind={'chevronRight'} />
                        <Button color={'neutral'} variant={'borderless'}>
                            <Text
                                sx={{ textDecoration: 'underline' }}
                                color={'inherit'}
                                noWrap
                                title={subtask.title || 'Untitled Subtask'}
                                truncate={30}
                                variant={'p2'}
                            >
                                {subtask.title || 'Untitled Subtask'}
                            </Text>
                        </Button>
                    </>
                )}
            </BreadcrumbsStack>
        ) : null;
    };

    useEffect(() => {
        if (!isOpen)
            setTimeout(() => {
                setView('task');
                setActiveSubtaskId('');
            }, 100);
    }, [isOpen]);

    return (
        <>
            <Stack spacing={2}>
                <IconButton
                    aria-label={'close'}
                    data-cy={'template-back-button'}
                    kind={'arrowLeft'}
                    size={'medium'}
                    variant={'square'}
                    onClick={handleClickBack}
                />
                {renderBreadcrumbs()}
                {view === 'task' ? (
                    <TemplateTaskView
                        templateState={templateState}
                        template={template}
                        files={files}
                        sharedUrls={sharedUrls}
                        changes={changes}
                        groups={groups}
                        spawnedTasks={spawnedTasks}
                        updateTemplate={handleTemplateChange}
                        onAddResources={handleAddResources}
                        onDeleteResource={handleDeleteResource}
                        onClickSubtask={handleClickSubtask}
                        onAddSubtask={handleAddSubtask}
                        onDeleteSubtask={handleDeleteSubtask}
                    />
                ) : (
                    <TemplateSubtaskView
                        subtask={
                            template.subtasks?.find((subtask) => subtask.id === activeSubtaskId) ||
                            changes.subtasks?.find((subtask) => subtask.id === activeSubtaskId) ||
                            {}
                        }
                        changes={changes}
                        updateSubtask={handleSubtaskChange}
                    />
                )}
            </Stack>
            <ControlsStack direction={'row'} justifyContent={'space-between'}>
                <DrawerActions
                    isRenderedInMilestone={isRenderedInMilestone}
                    templateState={templateState}
                    template={template}
                    changes={changes}
                    setView={setView}
                    setActiveSubtaskId={setActiveSubtaskId}
                    setFiles={setFiles}
                    setSharedUrls={setSharedUrls}
                    setChanges={setChanges}
                    setDrawerState={setDrawerState}
                    setConfirmationModal={setConfirmationModal}
                    closeConfirmationModal={closeConfirmationModal}
                    setScheduleModal={setScheduleModal}
                    save={save}
                    publish={publish}
                    unpublish={unpublish}
                    unschedule={unschedule}
                    deleteTemplate={deleteTemplate}
                />
            </ControlsStack>
        </>
    );
};

const BreadcrumbsStack = styled(Stack)(() => {
    return {
        flexWrap: 'wrap',
    };
});

const ControlsStack = styled(Stack)(({ theme }) => {
    return {
        position: 'absolute',
        bottom: '0',
        borderTop: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        margin: theme.spacing(0, -5),
        width: '100%',
        maxHeight: '69px',
        padding: theme.spacing(4),
        backgroundColor: theme.palette.ensightful.shades[0],
    };
});

export default TemplateDrawerInner;
